import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Space,
  Alert,
  Tabs,
  Empty,
  Calendar,
  Badge,
  Skeleton,
  Spin
} from 'antd';
import { 
  ClockCircleOutlined,
  CheckSquareOutlined,
  FireOutlined,
  TrophyOutlined,
  CalendarOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { useAuthState } from 'react-firebase-hooks/auth';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { auth } from '../api/firebase';
import { getTodayPomodoroSessions, getWeeklyPomodoroSessions } from '../api/services/pomodoroService';
import { getUserHabits, getHabitAnalytics } from '../api/services/habitService';

const { Title, Text } = Typography;

const AnalyticsPanel = () => {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    pomodoro: {
      todaySessions: 0,
      weeklyData: [],
      totalMinutes: 0
    },
    habits: {
      totalHabits: 0,
      completedToday: 0,
      averageCompletion: 0,
      streakData: []
    },
    overall: {
      productivityScore: 0,
      weeklyTrend: 'up',
      pomodoroContribution: 0,
      habitContribution: 0
    }
  });

  const COLORS = ['#1E90FF', '#52c41a', '#FFA500', '#FF4500', '#722ed1', '#eb2f96'];

  useEffect(() => {
    if (user) {
      loadAnalyticsData();
    }
  }, [user]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadPomodoroAnalytics(),
        loadHabitAnalytics()
      ]);
      calculateOverallStats();
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPomodoroAnalytics = async () => {
    try {
      const [todaySessions, weeklyData] = await Promise.all([
        getTodayPomodoroSessions(user.uid),
        getWeeklyPomodoroSessions(user.uid)
      ]);

      const totalMinutes = todaySessions.reduce((total, session) => {
        if (session.duration && typeof session.duration === 'number') {
          return total + Math.round(session.duration / 60);
        }
        return total;
      }, 0);

      const chartData = Object.entries(weeklyData.dailyStats || {}).map(([date, count]) => ({
        date: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
        sessions: count,
        minutes: count * 25
      }));

      setAnalytics(prev => ({
        ...prev,
        pomodoro: {
          todaySessions: todaySessions.length,
          weeklyData: chartData,
          totalMinutes
        }
      }));
    } catch (error) {
      console.error('Error loading Pomodoro analytics:', error);
    }
  };

  const loadHabitAnalytics = async () => {
    try {
      const habits = await getUserHabits(user.uid);
      
      if (habits.length === 0) {
        return;
      }

      let completedToday = 0;
      let totalCompletion = 0;
      const streakData = [];

      for (const habit of habits) {
        try {
          const habitAnalytics = await getHabitAnalytics(user.uid, habit.id, 7);
          
          const todayCompletion = habitAnalytics.dailyData?.[habitAnalytics.dailyData.length - 1]?.completed || 0;
          if (todayCompletion > 0) completedToday++;
          
          totalCompletion += habitAnalytics.completionRate || 0;
          
          streakData.push({
            name: habit.name,
            streak: habitAnalytics.currentStreak || 0,
            completion: habitAnalytics.completionRate || 0,
            color: habit.color
          });
        } catch (error) {
          console.error(`Error loading analytics for habit ${habit.id}:`, error);
        }
      }

      const averageCompletion = habits.length > 0 ? Math.round(totalCompletion / habits.length) : 0;

      setAnalytics(prev => ({
        ...prev,
        habits: {
          totalHabits: habits.length,
          completedToday,
          averageCompletion,
          streakData: streakData.sort((a, b) => b.streak - a.streak)
        }
      }));
    } catch (error) {
      console.error('Error loading habit analytics:', error);
    }
  };

  const calculateOverallStats = () => {
    setAnalytics(prev => {
      const pomodoroScore = Math.min(prev.pomodoro.todaySessions * 10, 70);
      
      const habitScore = prev.habits.totalHabits > 0 
        ? Math.min((prev.habits.completedToday / prev.habits.totalHabits) * 30, 30)
        : 0;
      
      const productivityScore = Math.round(pomodoroScore + habitScore);
      
      return {
        ...prev,
        overall: {
          productivityScore,
          weeklyTrend: productivityScore > 70 ? 'up' : productivityScore > 40 ? 'stable' : 'down',
          pomodoroContribution: pomodoroScore,
          habitContribution: habitScore
        }
      };
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#FFA500';
    if (score >= 40) return '#1E90FF';
    return '#FF4500';
  };

  const getScoreStatus = (score) => {
    if (score >= 80) return 'Excellent! 🎉';
    if (score >= 60) return 'Great work! 👍';
    if (score >= 40) return 'Good progress 📈';
    return 'Let\'s improve! 💪';
  };

  const OverviewCards = () => (
    <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <>
              <Statistic
                title="Today's Focus Sessions"
                value={analytics.pomodoro.todaySessions}
                prefix={<ClockCircleOutlined style={{ color: '#1E90FF' }} />}
                valueStyle={{ color: '#1E90FF' }}
                suffix="sessions"
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {analytics.pomodoro.totalMinutes} minutes focused
              </Text>
            </>
          )}
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card style={{ opacity: analytics.habits.totalHabits > 0 ? 1 : 0.5 }}>
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <>
              <Statistic
                title="Habits Completed Today"
                value={analytics.habits.completedToday}
                prefix={<CheckSquareOutlined style={{ color: analytics.habits.totalHabits > 0 ? '#52c41a' : '#d9d9d9' }} />}
                valueStyle={{ color: analytics.habits.totalHabits > 0 ? '#52c41a' : '#d9d9d9' }}
                suffix={`/ ${analytics.habits.totalHabits}`}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {analytics.habits.totalHabits > 0 
                  ? `${analytics.habits.averageCompletion}% average completion`
                  : 'No habits tracked yet'
                }
              </Text>
            </>
          )}
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card style={{ opacity: analytics.habits.streakData.length > 0 ? 1 : 0.5 }}>
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <>
              <Statistic
                title="Longest Streak"
                value={analytics.habits.streakData[0]?.streak || 0}
                prefix={<FireOutlined style={{ color: analytics.habits.streakData.length > 0 ? '#FFA500' : '#d9d9d9' }} />}
                valueStyle={{ color: analytics.habits.streakData.length > 0 ? '#FFA500' : '#d9d9d9' }}
                suffix="days"
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {analytics.habits.streakData[0]?.name || 'No streaks yet'}
              </Text>
            </>
          )}
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <>
              <Statistic
                title="Productivity Score"
                value={analytics.overall.productivityScore}
                prefix={<TrophyOutlined style={{ color: getScoreColor(analytics.overall.productivityScore) }} />}
                valueStyle={{ color: getScoreColor(analytics.overall.productivityScore) }}
                suffix="/ 100"
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Focus: {analytics.overall.pomodoroContribution || 0}/70 • Habits: {Math.round(analytics.overall.habitContribution || 0)}/30
              </Text>
            </>
          )}
        </Card>
      </Col>
    </Row>
  );

  const PomodoroCharts = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <Card title="Weekly Focus Sessions">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : analytics.pomodoro.weeklyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analytics.pomodoro.weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="sessions" fill="#1E90FF" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="No Pomodoro data yet" />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Focus Time Distribution">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : analytics.pomodoro.weeklyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.pomodoro.weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Line 
                  type="monotone" 
                  dataKey="minutes" 
                  stroke="#1E90FF" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="No focus time data yet" />
          )}
        </Card>
      </Col>
    </Row>
  );

  const HabitCharts = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <Card title="Habit Completion Rates">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : analytics.habits.streakData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={analytics.habits.streakData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="completion"
                  label={({ name, completion }) => `${name}: ${completion}%`}
                >
                  {analytics.habits.streakData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="No habit data yet" />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Current Streaks">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : analytics.habits.streakData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analytics.habits.streakData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <RechartsTooltip />
                <Bar dataKey="streak" fill="#FFA500" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="No streak data yet" />
          )}
        </Card>
      </Col>
    </Row>
  );

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Alert
          message="Authentication Required"
          description="Please sign in to view your analytics."
          type="info"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ color: '#000080', marginBottom: '8px' }}>
          Analytics Dashboard
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Track your productivity journey across all apps
        </Text>
      </div>

      <OverviewCards />

      {/* Additional Analytics Insights */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={8}>
          <Card size="small">
            {loading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <>
                <Statistic
                  title="Weekly Focus Time"
                  value={analytics.pomodoro.weeklyData.reduce((sum, day) => sum + (day.minutes || 0), 0)}
                  suffix="min"
                  valueStyle={{ color: '#722ed1', fontSize: '18px' }}
                />
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  Total focus time this week
                </Text>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card size="small">
            {loading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <>
                <Statistic
                  title="Average Session"
                  value={
                    analytics.pomodoro.todaySessions > 0 
                      ? Math.round(analytics.pomodoro.totalMinutes / analytics.pomodoro.todaySessions)
                      : 0
                  }
                  suffix="min"
                  valueStyle={{ color: '#eb2f96', fontSize: '18px' }}
                />
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  Average length today
                </Text>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card size="small">
            {loading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <>
                <Statistic
                  title="Most Productive Day"
                  value={
                    analytics.pomodoro.weeklyData.length > 0
                      ? analytics.pomodoro.weeklyData.reduce((max, day) => 
                          day.sessions > max.sessions ? day : max, 
                          { date: 'N/A', sessions: 0 }
                        ).date
                      : 'N/A'
                  }
                  valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                  suffix={
                    analytics.pomodoro.weeklyData.length > 0
                      ? `(${analytics.pomodoro.weeklyData.reduce((max, day) => 
                          day.sessions > max.sessions ? day : max, 
                          { sessions: 0 }
                        ).sessions})`
                      : ''
                  }
                />
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  This week's best day
                </Text>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Tabs 
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: (
              <Space>
                <RiseOutlined />
                Overview
              </Space>
            ),
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {analytics.overall.productivityScore > 0 && (
                  <Card 
                    title="Productivity Insights"
                    style={{ background: `linear-gradient(135deg, ${getScoreColor(analytics.overall.productivityScore)}10, ${getScoreColor(analytics.overall.productivityScore)}05)` }}
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={12}>
                        <div style={{ textAlign: 'center' }}>
                          <Title level={1} style={{ 
                            color: getScoreColor(analytics.overall.productivityScore),
                            margin: 0,
                            fontSize: '48px'
                          }}>
                            {analytics.overall.productivityScore}
                          </Title>
                          <Text style={{ fontSize: '16px', color: '#4B4B4B' }}>
                            Productivity Score
                          </Text>
                          <div style={{ marginTop: '8px' }}>
                            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              Focus Sessions (up to 70): {analytics.overall.pomodoroContribution || 0} pts<br/>
                              Habit Completion (up to 30): {Math.round(analytics.overall.habitContribution || 0)} pts
                            </Text>
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} md={12}>
                        <Space direction="vertical">
                          <Text strong>Today's Highlights:</Text>
                          <Text>• Completed {analytics.pomodoro.todaySessions} focus sessions</Text>
                          <Text>• Focused for {analytics.pomodoro.totalMinutes} minutes total</Text>
                          {analytics.pomodoro.todaySessions > 0 && (
                            <Text>• Average session: {Math.round(analytics.pomodoro.totalMinutes / analytics.pomodoro.todaySessions)} minutes</Text>
                          )}
                          <Text>• Finished {analytics.habits.completedToday} out of {analytics.habits.totalHabits} habits</Text>
                          {analytics.habits.streakData[0] && (
                            <Text>• Best streak: {analytics.habits.streakData[0].streak} days ({analytics.habits.streakData[0].name})</Text>
                          )}
                          {analytics.pomodoro.weeklyData.length > 0 && (
                            <Text>• Weekly total: {analytics.pomodoro.weeklyData.reduce((sum, day) => sum + (day.sessions || 0), 0)} sessions</Text>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                )}
                
                <Row gutter={[24, 24]}>
                  <Col span={24}>
                    <PomodoroCharts />
                  </Col>
                  <Col span={24}>
                    <HabitCharts />
                  </Col>
                </Row>
              </Space>
            )
          },
          {
            key: 'pomodoro',
            label: (
              <Space>
                <ClockCircleOutlined />
                Focus Analytics
              </Space>
            ),
            children: <PomodoroCharts />
          },
          {
            key: 'habits',
            label: (
              <Space>
                <CheckSquareOutlined />
                Habit Analytics
              </Space>
            ),
            children: <HabitCharts />
          }
        ]}
      />
    </div>
  );
};

export default AnalyticsPanel; 